'use client';

import React, { createContext, useContext, useMemo } from 'react';

/**
 * AppShellContext — the few facts a shared chrome component needs from the app
 * hosting it, supplied as data rather than reached for through an app-resolved
 * `@/hooks/*` import.
 *
 * WHY. `UnifiedPageLayout` imported `@/hooks/useWorkspaceSwitcher` — a module
 * that exists only in the console — purely to learn one string: the workspace
 * name, so the breadcrumb could drop a crumb that merely repeats the workspace
 * switcher rendered above it. That import made THE canonical page chrome
 * unusable in any other app without stubbing a console hook, which is why the
 * staff app hand-rolled its own page headers instead of using it (2026-08-17).
 *
 * Keep this SMALL. It is not an app-state store: it carries only what a shared
 * chrome component genuinely cannot derive, and every field is optional so a
 * shell that has no concept of a workspace simply doesn't provide one.
 */
interface AppShellContextValue {
  /**
   * The workspace the shell's own switcher is already displaying. Any
   * breadcrumb crumb whose label equals this (case-insensitive) is dropped —
   * the switcher IS that crumb, and an entity sharing the workspace's name
   * otherwise renders the same word twice on the title row.
   */
  workspaceCrumbLabel?: string | null;
}

const AppShellContext = createContext<AppShellContextValue>({});

export function AppShellProvider({
  workspaceCrumbLabel,
  children,
}: AppShellContextValue & { children: React.ReactNode }) {
  const value = useMemo(() => ({ workspaceCrumbLabel }), [workspaceCrumbLabel]);
  return <AppShellContext.Provider value={value}>{children}</AppShellContext.Provider>;
}

/** Always safe to call — an app with no provider gets an empty shell context. */
export function useAppShell(): AppShellContextValue {
  return useContext(AppShellContext);
}
